import React, { useState, useEffect } from 'react';
import { 
  WifiIcon, 
  SignalSlashIcon, 
  CloudArrowUpIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { offlineManager } from '../utils/offlineManager';

const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingMatches, setPendingMatches] = useState(0);
  const [issyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    const handleSyncComplete = (event: CustomEvent) => {
      setPendingMatches(0);
      setIsSyncing(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offline-sync-complete', handleSyncComplete as EventListener);

    // Check for pending matches on mount
    const checkPendingMatches = async () => {
      try {
        const pending = await offlineManager.getPendingMatches();
        setPendingMatches(pending.length);
      } catch (error) {
        console.error('Failed to check pending matches:', error);
      }
    };

    checkPendingMatches();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offline-sync-complete', handleSyncComplete as EventListener);
    };
  }, []);

  const handleSync = async () => {
    if (!isOnline) return;
    
    setIsSyncing(true);
    try {
      await offlineManager.syncPendingMatches();
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  if (isOnline && pendingMatches === 0) {
    return (
      <div className="flex items-center text-success-600">
        <WifiIcon className="h-4 w-4 mr-1" />
        <span className="text-sm">Online</span>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="flex items-center text-warning-600">
        <SignalSlashIcon className="h-4 w-4 mr-1" />
        <span className="text-sm">Offline</span>
        {pendingMatches > 0 && (
          <span className="ml-1 text-xs bg-warning-100 text-warning-800 px-2 py-0.5 rounded-full">
            {pendingMatches} pending
          </span>
        )}
      </div>
    );
  }

  // Online but has pending matches
  return (
    <div className="flex items-center">
      <button
        onClick={handleSync}
        disabled={issyncing}
        className="flex items-center text-primary-600 hover:text-primary-700 disabled:opacity-50"
      >
        {issyncing ? (
          <>
            <div className="animate-spin h-4 w-4 mr-1">
              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <span className="text-sm">Syncing...</span>
          </>
        ) : (
          <>
            <CloudArrowUpIcon className="h-4 w-4 mr-1" />
            <span className="text-sm">Sync {pendingMatches}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default OfflineIndicator;